import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useScreenWakeLock from '../../hooks/useScreenWakeLock';
import './AudioPlayer.css';

// Target words per minute for natural speech speed
const TARGET_WPM = 150;

// Special value to indicate "natural speed" mode
export const NATURAL_SPEED = -1;

interface AudioPlayerProps {
  audioUrl?: string;
  onPlay?: () => void;
  onPause?: () => void;
  onSpeedChange?: (speed: number) => void;
  onRepeat?: () => void;
  // Course's speaking WPM (from Course entity, calculated from SRT timings)
  speakingWpm?: number;
  // User preference for auto natural speed
  autoNaturalSpeed?: boolean;
  // Controlled speed/repeat state (for persisting across sentences)
  speed?: number;
  repeating?: boolean;
  onRepeatingChange?: (repeating: boolean) => void;
}

export interface AudioPlayerRef {
  togglePlayPause: () => void;
  play: () => void;
  pause: () => void;
  isPlaying: boolean;
}

// Calculate playback rate to achieve target WPM based on course's speaking WPM
export const calculateNaturalSpeedRate = (speakingWpm: number): number => {
  if (!speakingWpm || speakingWpm <= 0) return 1;

  // Calculate required playback rate
  // If course's WPM is 120 and target is 150 WPM, rate = 150/120 = 1.25
  const rate = TARGET_WPM / speakingWpm;

  // Clamp between 0.5 and 2.0 for reasonable playback
  return Math.max(0.5, Math.min(2.0, rate));
};

const AudioPlayer = forwardRef<AudioPlayerRef, AudioPlayerProps>(({
  audioUrl,
  onPlay,
  onPause,
  onSpeedChange,
  onRepeat,
  speakingWpm,
  autoNaturalSpeed = false,
  speed: controlledSpeed,
  repeating: controlledRepeating,
  onRepeatingChange,
}, ref) => {
  const { t } = useTranslation('learning');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [internalSpeed, setInternalSpeed] = useState<number>(autoNaturalSpeed ? NATURAL_SPEED : 1);
  const [internalRepeating, setInternalRepeating] = useState(false);

  useScreenWakeLock(isPlaying);

  // Use controlled state when provided, otherwise use internal state
  const currentSpeed = controlledSpeed !== undefined ? controlledSpeed : internalSpeed;
  const isRepeating = controlledRepeating !== undefined ? controlledRepeating : internalRepeating;

  // Speed options: -1 represents "natural speed"
  const speeds = [NATURAL_SPEED, 0.5, 0.75, 1, 1.25, 1.5];

  // Calculate the actual playback rate
  const getActualPlaybackRate = useCallback((speed: number): number => {
    if (speed === NATURAL_SPEED) {
      if (speakingWpm) {
        return calculateNaturalSpeedRate(speakingWpm);
      }
      return 1; // Fallback to 1x if no speakingWpm available
    }
    return speed;
  }, [speakingWpm]);

  // Get display text for speed
  const getSpeedDisplayText = (speed: number): string => {
    if (speed === NATURAL_SPEED) {
      return t('audioPlayer.naturalSpeed');
    }
    return `${speed}x`;
  };

  // Update speed when autoNaturalSpeed preference changes
  useEffect(() => {
    if (autoNaturalSpeed) {
      setInternalSpeed(NATURAL_SPEED);
    }
  }, [autoNaturalSpeed]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = getActualPlaybackRate(currentSpeed);
    }
  }, [currentSpeed, getActualPlaybackRate]);

  // Reset playing state when audio source changes
  useEffect(() => {
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [audioUrl]);

  // Apply playback rate when audio loads (handles new audio sources)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      audio.playbackRate = getActualPlaybackRate(currentSpeed);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
  }, [currentSpeed, getActualPlaybackRate]);

  // Sync the native loop property so the browser handles repeat internally
  // (avoids mobile autoplay restrictions that block play() inside 'ended' callbacks)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.loop = isRepeating;
  }, [isRepeating]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      // When loop is true the browser restarts automatically — 'ended' won't fire.
      // This handler only runs when loop is off.
      setIsPlaying(false);
    };

    audio.addEventListener('ended', handleEnded);
    return () => audio.removeEventListener('ended', handleEnded);
  }, []);

  const safePlay = useCallback((audio: HTMLAudioElement) => {
    const p = audio.play();
    if (p) p.catch(() => { setIsPlaying(false); });
  }, []);

  const handlePlayPause = useCallback(() => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      onPause?.();
    } else {
      safePlay(audioRef.current);
      setIsPlaying(true);
      onPlay?.();
    }
  }, [isPlaying, onPause, onPlay, safePlay]);

  const play = useCallback(() => {
    if (!audioRef.current || isPlaying) return;
    safePlay(audioRef.current);
    setIsPlaying(true);
    onPlay?.();
  }, [isPlaying, onPlay, safePlay]);

  const pause = useCallback(() => {
    if (!audioRef.current || !isPlaying) return;
    audioRef.current.pause();
    setIsPlaying(false);
    onPause?.();
  }, [isPlaying, onPause]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    togglePlayPause: handlePlayPause,
    play,
    pause,
    isPlaying,
  }), [handlePlayPause, play, pause, isPlaying]);

  const handleSpeedClick = () => {
    const currentIdx = speeds.indexOf(currentSpeed);
    const nextIndex = (currentIdx + 1) % speeds.length;
    const newSpeed = speeds[nextIndex];
    setInternalSpeed(newSpeed);
    onSpeedChange?.(newSpeed);
  };

  const handleRepeatClick = () => {
    const newRepeating = !isRepeating;
    setInternalRepeating(newRepeating);
    onRepeatingChange?.(newRepeating);
    onRepeat?.();
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && e.target === document.body) {
        e.preventDefault();
        handlePlayPause();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handlePlayPause]);

  return (
    <div className="audio-player">
      {audioUrl && (
        <audio ref={audioRef} src={audioUrl} preload="metadata" />
      )}

      <button
        className={`audio-player__button audio-player__play ${isPlaying ? 'audio-player__play--playing' : ''}`}
        onClick={handlePlayPause}
        disabled={!audioUrl}
      >
        {isPlaying ? (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="2" y="1" width="3.5" height="12" rx="1" /><rect x="8.5" y="1" width="3.5" height="12" rx="1" /></svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><path d="M3 1.5v11l9-5.5z" /></svg>
        )}
        {isPlaying ? t('audioPlayer.pause') : t('audioPlayer.play')}
      </button>

      <button
        className={`audio-player__button audio-player__repeat ${isRepeating ? 'audio-player__repeat--active' : ''}`}
        onClick={handleRepeatClick}
        disabled={!audioUrl}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1.5 7a5.5 5.5 0 0 1 9.5-3.5M12.5 7a5.5 5.5 0 0 1-9.5 3.5" />
          <path d="M11 1v3h-3" /><path d="M3 10v3h3" />
        </svg>
        {t('audioPlayer.repeat')}
      </button>

      <button
        className={`audio-player__button audio-player__speed ${currentSpeed === NATURAL_SPEED ? 'audio-player__speed--natural' : ''}`}
        onClick={handleSpeedClick}
        disabled={!audioUrl}
        title={currentSpeed === NATURAL_SPEED ? t('audioPlayer.naturalSpeedTooltip', { percent: Math.round(getActualPlaybackRate(currentSpeed) * 100) }) : undefined}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="7" cy="7" r="5.5" /><path d="M7 4v3l2 1.5" />
        </svg>
        {getSpeedDisplayText(currentSpeed)}
      </button>
    </div>
  );
});

export default AudioPlayer;